import { useEffect, useMemo, useState } from "react";

type VersionsResponse = {
  section3List?: Array<{
    versionInfo?: {
      versionNumber?: number;
      itemNoVersionId?: string;
      isCurrent?: boolean;
      updateDate?: string;
      status?: string;
      updateUserId?: string;
    };
    content?: Record<string, unknown>;
  }>;
};

export const useSectionThirdRecordVersions = <
  T extends Record<string, unknown> = Record<string, unknown>,
>(
  itemInfo: T | null | undefined,
  versionsData: VersionsResponse | undefined,
  itemNoVersionId: string | undefined,
  initialVersionNumber?: number,
  options?: { versionsAccessDenied?: boolean },
) => {
  const sortedVersionEntries = useMemo(() => {
    const list = versionsData?.section3List ?? [];
    const sorted = [...list].sort(
      (a, b) =>
        (b.versionInfo?.versionNumber ?? 0) -
        (a.versionInfo?.versionNumber ?? 0),
    );
    if (sorted.length > 0) {
      return sorted;
    }
    if (options?.versionsAccessDenied) {
      return [];
    }
    if (!itemInfo || !itemNoVersionId) {
      return [];
    }
    const info = itemInfo as Record<string, unknown>;
    const currentVersionId = info.currentVersionId as string | undefined;
    if (!currentVersionId) {
      return [];
    }
    const versionNumber =
      typeof info.currentVersionNumber === "number"
        ? info.currentVersionNumber
        : 1;
    return [
      {
        versionInfo: {
          versionNumber,
          itemNoVersionId: currentVersionId,
          isCurrent: true,
          updateDate: info.updateDate as string | undefined,
          status: info.status as string | undefined,
          updateUserId: info.updateUserId as string | undefined,
        },
        content: undefined as Record<string, unknown> | undefined,
      },
    ];
  }, [versionsData?.section3List, itemInfo, itemNoVersionId, options?.versionsAccessDenied]);

  const [selectedVersionNumber, setSelectedVersionNumber] = useState<
    number | undefined
  >(initialVersionNumber);

  useEffect(() => {
    setSelectedVersionNumber(initialVersionNumber);
  }, [initialVersionNumber, itemNoVersionId]);

  useEffect(() => {
    if (selectedVersionNumber !== undefined) return;
    const cur =
      sortedVersionEntries.find((v) => v.versionInfo?.isCurrent) ??
      sortedVersionEntries[0];
    const vn = cur?.versionInfo?.versionNumber;
    if (vn != null) {
      setSelectedVersionNumber(vn);
    }
  }, [sortedVersionEntries, selectedVersionNumber]);

  const selectedEntry = useMemo(() => {
    if (!sortedVersionEntries.length) return null;
    const vn =
      selectedVersionNumber ??
      sortedVersionEntries.find((v) => v.versionInfo?.isCurrent)
        ?.versionInfo?.versionNumber ??
      sortedVersionEntries[0]?.versionInfo?.versionNumber;
    if (vn == null) return sortedVersionEntries[0];
    return (
      sortedVersionEntries.find((v) => v.versionInfo?.versionNumber === vn) ??
      sortedVersionEntries[0]
    );
  }, [sortedVersionEntries, selectedVersionNumber]);

  const maxVersionNumber =
    sortedVersionEntries[0]?.versionInfo?.versionNumber ?? 0;

  const isViewingHistoricalVersion =
    maxVersionNumber > 0 &&
    (selectedEntry?.versionInfo?.versionNumber ?? 0) < maxVersionNumber;

  const displayItemInfo = useMemo((): T | null => {
    if (!itemInfo) return null;
    if (!selectedEntry?.versionInfo && !selectedEntry?.content) {
      return itemInfo;
    }
    const c = selectedEntry.content as Record<string, unknown> | undefined;
    const vi = selectedEntry.versionInfo;
    return {
      ...itemInfo,
      ...(c ?? {}),
      workName:
        (c?.workName as string | undefined) ??
        (itemInfo.workName as string | undefined),
      workCondition:
        (c?.workCondition as string | undefined) ??
        (itemInfo.workCondition as string | undefined),
      locationDescription:
        (c?.locationDescription as string | undefined) ??
        (itemInfo.locationDescription as string | undefined),
      locationType:
        (c?.locationType as string | undefined) ??
        (itemInfo.locationType as string | undefined),
      status:
        vi?.status ??
        (c?.status as string | undefined) ??
        (itemInfo.status as string | undefined),
      updateDate:
        vi?.updateDate ??
        (c?.updateDate as string | undefined) ??
        (itemInfo.updateDate as string | undefined),
      updateUserId:
        vi?.updateUserId ??
        (c?.updateUserId as string | undefined) ??
        (itemInfo.updateUserId as string | undefined),
      signer: c?.signer ?? itemInfo.signer,
      docs: Array.isArray(c?.docs) ? c.docs : itemInfo.docs,
      currentVersionId:
        vi?.itemNoVersionId ?? itemInfo.currentVersionId,
      sequenceNumber:
        (c?.sequenceNumber as number | undefined) ??
        (itemInfo.sequenceNumber as number | undefined),
    } as T;
  }, [itemInfo, selectedEntry]);

  return {
    sortedVersionEntries,
    selectedVersionNumber,
    setSelectedVersionNumber,
    selectedEntry,
    maxVersionNumber,
    isViewingHistoricalVersion,
    displayItemInfo,
  };
};
